import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';
import { MasterCodeSchema } from './query-types.js';

/**
 * Lost Reason master (Masters → Lost Reasons). The reason a lead was marked
 * lost (e.g. "budget", "booked_elsewhere", "no_response"). A pure simple
 * master - see ADR-0016 / ADR-0020 for the standard shape this follows.
 */

export const LostReasonListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** When true, marking a lead lost with this reason requires a budget. */
  requiresBudget: z.boolean(),
  /** When true, this reason is system/automatic-only: the auto-close cron may
   *  apply it, but agents cannot pick it and manual transitions carrying it are
   *  rejected. Defaults false for older payloads. */
  isAutoOnly: z.boolean().default(false),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type LostReasonListItem = z.infer<typeof LostReasonListItemSchema>;

export const LostReasonListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type LostReasonListQuery = z.infer<typeof LostReasonListQuerySchema>;

export const LostReasonListResponseSchema = paginatedResponseSchema(LostReasonListItemSchema);
export type LostReasonListResponse = z.infer<typeof LostReasonListResponseSchema>;

export const LostReasonDetailSchema = LostReasonListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type LostReasonDetail = z.infer<typeof LostReasonDetailSchema>;

const LostReasonCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().trim().max(500).nullable().optional(),
  /** When true, marking a lead lost with this reason requires a budget. */
  requiresBudget: z.boolean().optional(),
  /** System/automatic-only reason (hidden from agent dropdowns). */
  isAutoOnly: z.boolean().optional(),
  legacyId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateLostReasonRequestSchema = LostReasonCoreFieldsSchema;
export type CreateLostReasonRequest = z.infer<typeof CreateLostReasonRequestSchema>;

export const UpdateLostReasonRequestSchema = LostReasonCoreFieldsSchema.partial();
export type UpdateLostReasonRequest = z.infer<typeof UpdateLostReasonRequestSchema>;
